import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MoreHorizontal, Plus } from 'lucide-react';
import SearchFilter from './SearchFilter';
import InviteUserModal from './InviteUserModal';
import EditUserModal from './EditUserModal';
import {
  fetchTenantUsers,
  removeTenantUser,
  selectTenantUsers,
  selectTenantUsersLoading,
  selectTenantUsersError,
  selectTenantMutating,
  selectTenantMutateError,
} from '../../../store/adminTenantsSlice';

const VIEW_BY_OPTIONS = ['Edit', 'Remove'];
const theadStyle =
  'h-11 px-6 py-4 text-base font-medium text-[#757575] text-center align-middle bg-[#F7F7F7]';

export default function AdminTenants() {
  const dispatch = useDispatch();
  const tenantId = useSelector((s) => s.auth.tenantId);
  const users = useSelector(selectTenantUsers);
  const loading = useSelector(selectTenantUsersLoading);
  const error = useSelector(selectTenantUsersError);
  const mutating = useSelector(selectTenantMutating);
  const mutateError = useSelector(selectTenantMutateError);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [actionError, setActionError] = useState(null);
  const dropdownRef = useRef(null);

  const refreshUsers = () => {
    if (tenantId) dispatch(fetchTenantUsers(tenantId));
  };

  useEffect(() => {
    refreshUsers();
  }, [dispatch, tenantId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenRowIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInviteClose = () => {
    setShowInviteModal(false);
    refreshUsers();
  };

  const handleRemove = async (tenant) => {
    setActionError(null);
    if (!tenant?.userId) {
      setActionError('Missing user id — cannot remove');
      return;
    }
    const result = await dispatch(removeTenantUser(tenant.userId));
    if (removeTenantUser.fulfilled.match(result)) {
      refreshUsers();
    } else {
      setActionError(
        typeof result.payload === 'string'
          ? result.payload
          : 'Failed to remove user',
      );
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-medium text-[#1D2739] text-left">Tenants</h1>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 rounded-full bg-[#F5E424] px-5 py-2 text-sm font-semibold text-[#1D2739] hover:bg-[#E0D020]"
        >
          <Plus size={16} />
          Invite user
        </button>
      </div>

      {(error || actionError || mutateError) && (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error || actionError || mutateError}
          {error && (
            <button className="ml-3 underline" onClick={refreshUsers}>
              Retry
            </button>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg">
        <SearchFilter showFilterButton={false} showExpandIcon={false} expandData={users} />
      </div>

      <div className="flex flex-col gap-6 w-full mt-4">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th className={theadStyle}>User name</th>
                <th className={theadStyle}>Department</th>
                <th className={theadStyle}>Email</th>
                <th className={theadStyle}>User role</th>
                <th className={theadStyle}>Last Activity</th>
                <th className="h-11 px-6 py-4 bg-[#F7F7F7]"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[#7E7E7E]">
                    Loading users…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-[#7E7E7E]">
                    No users found for this tenant
                  </td>
                </tr>
              ) : (
                users.map((tenant, index) => {
                  const cellColor =
                    tenant.userRole === 'invited' ? 'rgba(29, 39, 57, 0.5)' : '#1D2739';
                  return (
                    <tr key={`${tenant.email}-${index}`}>
                      <td
                        className="h-16 px-6 py-4 text-base text-center align-middle border-b border-[#EEE] bg-white"
                        style={{ color: cellColor }}
                      >
                        {tenant.userName}
                      </td>
                      <td
                        className="h-16 px-6 py-4 text-base text-center align-middle border-b border-[#EEE] bg-white"
                        style={{ color: cellColor }}
                      >
                        {tenant.department}
                      </td>
                      <td
                        className="h-16 px-6 py-4 text-base text-center align-middle border-b border-[#EEE] bg-white"
                        style={{ color: cellColor }}
                      >
                        {tenant.email}
                      </td>
                      <td
                        className="h-16 px-6 py-4 text-base text-center align-middle border-b border-[#EEE] bg-white"
                        style={{ color: cellColor }}
                      >
                        {tenant.userRole}
                      </td>
                      <td
                        className="h-16 px-6 py-4 text-base text-center align-middle border-b border-[#EEE] bg-white"
                        style={{ color: cellColor }}
                      >
                        {tenant.lastActivity}
                      </td>
                      <td className="h-16 px-6 py-4 text-center align-middle border-b border-[#EEE] bg-white relative">
                        <button
                          onClick={() =>
                            setOpenRowIndex(openRowIndex === index ? null : index)
                          }
                          className="p-2 hover:bg-gray-100 rounded-full"
                          disabled={mutating}
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {openRowIndex === index && (
                          <div
                            ref={dropdownRef}
                            className="absolute right-6 top-14 z-20 w-32 bg-white border rounded-lg shadow-lg"
                          >
                            {VIEW_BY_OPTIONS.map((opt) => (
                              <button
                                key={opt}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                                onClick={() => {
                                  setOpenRowIndex(null);
                                  if (opt === 'Edit') setEditTenant(tenant);
                                  if (opt === 'Remove') handleRemove(tenant);
                                }}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showInviteModal && <InviteUserModal onClose={handleInviteClose} />}
      {editTenant && (
        <EditUserModal
          tenant={editTenant}
          onClose={() => setEditTenant(null)}
          onSuccess={refreshUsers}
        />
      )}
    </div>
  );
}
